import {
  CHANGE_ALLOWED_DOMAINS,
  CHANGE_AUTO_CONVERT_TRIAL,
  CHANGE_CANCELLATION_COMMENT,
  CHANGE_CANCELLATION_REASON,
  CHANGE_DMARC_FOR_PARTNERS,
  CHANGE_DMARC_PLAN,
  CHANGE_ENCRYPTION_LEVEL,
  CHANGE_ENFORCE_MAILBOXES_LIMIT,
  CHANGE_EXPIRATION_DATE,
  CHANGE_MAILBOX_LIMIT,
  CHANGE_PLAN,
  CHANGE_PLAN_TOGGLE_VALUES,
  CHANGE_SILENT_MODE_STATUS,
  CHANGE_TRIAL_EXPIRATION,
  CHANGE_TRIAL_PLAN,
  EDIT_TENANT_APPLY_ASYNC,
  EMAIL_ENCRYPTION_ADDON_NAME,
  FETCH_TENANT_DATA,
  TOGGLE_ADDON,
  TOGGLE_AUTOPILOT,
} from './constants'

export default function () {
  const normalizePlanValue = (value) => {
    const normalized = Number(value)
    return Number.isInteger(normalized) ? normalized : value
  }

  const initialState = {
    data: {},
    loader: false,
    error: null,
    originalData: {},
    mapSelectedAddons: {},
    originalMapSelectedAddons: {},
    touchedAddons: {},
  }

  return (state = initialState, action = {}) => {
    switch (action.type) {
      case TOGGLE_ADDON:
        return {
          ...state,
          mapSelectedAddons: {
            ...state.mapSelectedAddons,
            [action.payload.addonName]: !state.mapSelectedAddons[action.payload.addonName],
          },
          touchedAddons: {
            ...state.touchedAddons,
            [action.payload.addonName]: true,
          },
        }
      case CHANGE_ALLOWED_DOMAINS:
        return {
          ...state,
          data: {
            ...state.data,
            allowedDomains: action.payload.value,
          },
        }
      case CHANGE_PLAN: {
        const nextPlanType = normalizePlanValue(action.payload.value)
        const originalPlanType = normalizePlanValue(state.originalData.planType)
        const addonsByPlan = state?.data?.addons || {}
        const relevantAddons =
          addonsByPlan[String(nextPlanType)] || addonsByPlan[nextPlanType] || {}
        const hasChangedPlan = originalPlanType !== nextPlanType
        if (!hasChangedPlan) {
          return {
            ...state,
            data: {
              ...state.data,
              planType: nextPlanType,
            },
            // Returning to original plan should restore original addon baseline.
            mapSelectedAddons: { ...state.originalMapSelectedAddons },
            touchedAddons: {},
          }
        }
        const mapSelectedAddons = Object.values(relevantAddons).reduce((acc, addon) => {
          const addonName = addon?.name
          if (!addonName) return acc
          const isEditableAddon = addon?.editable && !addon?.disabled
          const isIncludedByPlan = Boolean(addon?.currentState || addon?.activeByDefault)
          acc[addonName] = isEditableAddon ? false : isIncludedByPlan
          return acc
        }, {})
        return {
          ...state,
          data: {
            ...state.data,
            planType: nextPlanType,
          },
          mapSelectedAddons,
          touchedAddons: {},
        }
      }
      case CHANGE_MAILBOX_LIMIT:
        return {
          ...state,
          data: {
            ...state.data,
            mailboxLimit: action.payload.value,
          },
        }
      case CHANGE_PLAN_TOGGLE_VALUES:
        return {
          ...state,
          data: {
            ...state.data,
            planToggleValues: action.payload.value,
          },
        }
      case CHANGE_DMARC_FOR_PARTNERS:
        return {
          ...state,
          data: {
            ...state.data,
            enableDmarcForPartnersProps: {
              ...state.data.enableDmarcForPartnersProps,
              value: action.payload.value,
            },
          },
        }
      case CHANGE_DMARC_PLAN:
        return {
          ...state,
          data: {
            ...state.data,
            dmarcPlan: action.payload.value,
          },
        }
      case CHANGE_ENCRYPTION_LEVEL:
        return {
          ...state,
          data: {
            ...state.data,
            emailEncryptionProps: {
              ...state.data.emailEncryptionProps,
              level: action.payload.value,
            },
          },
        }
      case CHANGE_EXPIRATION_DATE:
        return {
          ...state,
          data: {
            ...state.data,
            planExpiration: action.payload.value,
          },
        }
      case CHANGE_SILENT_MODE_STATUS:
        return {
          ...state,
          data: {
            ...state.data,
            silentMode: action.payload.value,
          },
        }
      case CHANGE_CANCELLATION_REASON:
        return {
          ...state,
          data: {
            ...state.data,
            cancellationReason: action.payload.value,
          },
        }
      case CHANGE_CANCELLATION_COMMENT:
        return {
          ...state,
          data: {
            ...state.data,
            cancellationComment: action.payload.value,
          },
        }
      case TOGGLE_AUTOPILOT:
        return {
          ...state,
          data: {
            ...state.data,
            isAutopilotEnabled: !state.data.isAutopilotEnabled,
          },
        }
      case CHANGE_TRIAL_EXPIRATION:
        return {
          ...state,
          data: {
            ...state.data,
            trialExpiration: action.payload.value,
          },
        }
      case CHANGE_TRIAL_PLAN:
        return {
          ...state,
          data: {
            ...state.data,
            trialPlanType: action.payload.value,
          },
        }
      case CHANGE_ENFORCE_MAILBOXES_LIMIT:
        return {
          ...state,
          data: {
            ...state.data,
            enforceMailboxesLimit: action.payload.value,
          },
        }
      case CHANGE_AUTO_CONVERT_TRIAL:
        return {
          ...state,
          data: {
            ...state.data,
            autoConvertTrial: action.payload.value,
          },
        }
      case FETCH_TENANT_DATA.INITIATE:
        return {
          ...state,
          loader: true,
          error: null,
        }
      case FETCH_TENANT_DATA.SUCCESS: {
        const { data } = action.payload
        const relevantAddons =
          data?.addons?.[String(data?.planType)] || data?.addons?.[data?.planType] || {}
        const mapSelectedAddons = Object.values(relevantAddons).reduce((acc, addon) => {
          acc[addon.name] = addon.currentState
          return acc
        }, {})
        const eeProps = data.emailEncryptionProps
        if (EMAIL_ENCRYPTION_ADDON_NAME in mapSelectedAddons) {
          mapSelectedAddons[EMAIL_ENCRYPTION_ADDON_NAME] = Boolean(
            eeProps?.encryptionActive || eeProps?.dlpActive
          )
        }
        return {
          ...state,
          loader: false,
          data,
          originalData: data,
          mapSelectedAddons,
          originalMapSelectedAddons: { ...mapSelectedAddons },
          touchedAddons: {},
        }
      }
      case FETCH_TENANT_DATA.ERROR:
        return {
          ...state,
          loader: false,
          error: action.error,
        }
      case FETCH_TENANT_DATA.CANCEL:
        return {
          ...state,
          data: {},
          loader: false,
          error: null,
          originalData: {},
          touchedAddons: {},
        }
      case EDIT_TENANT_APPLY_ASYNC.INITIATE:
        return {
          ...state,
          loader: true,
          error: null,
        }
      case EDIT_TENANT_APPLY_ASYNC.SUCCESS:
        return {
          ...state,
          loader: false,
          data: {
            ...state.data,
            ...action.payload.data,
          },
        }
      case EDIT_TENANT_APPLY_ASYNC.ERROR:
        return {
          ...state,
          loader: false,
          error: action.error,
        }
      case EDIT_TENANT_APPLY_ASYNC.CANCEL:
        return {
          ...state,
          loader: false,
          error: null,
        }
      default:
        return state
    }
  }
}
